import { Eye, Clock, Star, Users, ExternalLink, ImageOff } from 'lucide-react'
import { sanitizeHtml } from '../../lib/sanitize'

interface ContentPhonePreviewProps {
  title: string
  summary: string
  categoryName: string
  parentCategoryName: string
  audienceName: string
  author: string
  source: string
  bodyHtml: string
  mediaType: 'text' | 'video' | 'audio'
  mediaUrl?: string | null
  linkUrl?: string
  recommended?: boolean
}

/**
 * 内容编辑页左侧的「手机预览」。
 *
 * 目的：编辑时就能看到这条内容在用户端（小程序/H5 详情页）长什么样，
 * 避免「后台填得挺好、发布后排版走样」。
 *
 * ⚠️ **刻意不渲染封面图**（2026-09-19）：
 *   用户端详情页 `pages/user/KnowledgeDetail.tsx` 的结构是
 *   「标题卡片 → 摘要 → 音频/视频 → 正文卡片」，**根本没有主封面** ——
 *   封面只出现在列表卡片（`ContentCard`）里，详情接口虽然有 cover_url，
 *   但详情页没用它。
 *   所以预览里放一块封面 = 展示一个用户端看不到的东西，反而误导编辑
 *   （封面仍然在后台的「封面图」区设置，也仍然用于列表卡片）。
 *   若将来详情页要加封面，这里再补回来，保持两边同步。
 *
 * 说明：这里是**模拟**呈现，不是真实渲染器 —— 布局刻意贴着详情页走，
 * 正文沿用同一套 `.content-body` 排版类，所以图片居中、表格边框等
 * 与真实用户端一致；改动详情页样式时，这里也应同步。
 */
const ContentPhonePreview = ({
  title, categoryName, parentCategoryName, audienceName,
  author, source, bodyHtml, mediaType, mediaUrl, linkUrl, recommended,
  /* summary 不再解构展示（2026-09-19 业务要求，与用户端详情页同步）：
     Props 里保留该字段，ContentEditor 仍传入，将来要恢复展示直接加回来 */
}: ContentPhonePreviewProps) => {
  const hasBody = !!bodyHtml && bodyHtml.replace(/<[^>]*>/g, '').trim() !== ''
  const tags = [parentCategoryName, categoryName !== parentCategoryName ? categoryName : ''].filter(Boolean)
  const today = new Date().toISOString().slice(0, 10)

  return (
    /* 高度策略：整块预览不写死高度，而是「跟着父级（编辑页左侧 aside）走」——
       aside 用 `min-h(min(600px, 100vh-2rem))` + `max-h(min(700px, 100vh-2rem))`
       把高度夹在一个区间里，这里的 `flex-1 / min-h-0` 链把这份高度传给壳内滚动区：
         · 内容短 → 滚动区被撑开到区间下限，底部留白（不会再被压扁成一条）
         · 内容长 → 滚动区封顶，正文在壳内滚动
       视窗比 632px 还矮时，aside 的 min/max 同时收敛到 (100vh - 2rem)，
       预览变成一个固定高度的块，不会被顶出屏幕。
       ⚠️ 每一层都要带 `min-h-0`：flex 项目的 min-height 默认是 auto，
          不加的话内层会被内容撑开、max-height 形同虚设。 */
    <div className="w-[332px] flex flex-col min-h-0">
      <div className="flex items-center justify-between px-1 mb-2 shrink-0">
        <span className="text-xs font-medium text-gray-500">手机端预览</span>
        <span className="text-[10px] text-gray-400 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
          实时同步
        </span>
      </div>

      {/* 手机外壳 */}
      <div className="rounded-[2.2rem] bg-gray-900 p-2.5 shadow-xl flex-1 min-h-0 flex flex-col">
        <div className="rounded-[1.75rem] overflow-hidden bg-gray-100 flex-1 min-h-0 flex flex-col">
          {/* 状态栏 + 导航栏 */}
          <div className="bg-white px-3.5 pt-1.5 pb-2 shrink-0">
            <div className="flex items-center justify-between text-[9px] text-gray-400 leading-none">
              <span>9:41</span>
              <span className="tracking-tight">●●● ▮</span>
            </div>
            <div className="text-center text-[12px] font-medium text-gray-800 mt-1.5">文章详情</div>
          </div>

          {/* 可滚动内容区：高度由剩余空间决定，不再是写死的 540px */}
          <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50 thin-scroll">
            <div className="p-2.5 space-y-2">
              {/* 标题 + 元信息 */}
              <div className="bg-white rounded-xl px-3 py-3 space-y-2">
                <div className="flex flex-wrap items-center gap-1">
                  {recommended && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                      <Star className="w-2 h-2" />推荐
                    </span>
                  )}
                  {tags.map((t, i) => (
                    <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{t}</span>
                  ))}
                  {audienceName && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600">
                      <Users className="w-2 h-2" />{audienceName}
                    </span>
                  )}
                </div>
                <h1 className="text-[15px] font-bold text-gray-900 leading-snug">
                  {title || <span className="text-gray-300 font-normal">（未填写标题）</span>}
                </h1>
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[9px] text-gray-400">
                  {author && <span>作者：{author}</span>}
                  {source && <span>来源：{source}</span>}
                  <span className="inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{today}</span>
                  <span className="inline-flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />0 阅读</span>
                </div>
              </div>

              {/* 外链提示（2026-09-19 隐藏）：外链编辑入口已停用，存量外链内容
                  在用户端的真实跳转行为不变；若恢复编辑入口，同步解除本段注释。
              {linkUrl && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl px-3 py-2.5">
                  <div className="flex items-center gap-1.5 text-[11px] font-medium text-blue-700">
                    <ExternalLink className="w-3 h-3" />外链文章
                  </div>
                  <p className="text-[10px] text-blue-600/80 mt-1 break-all leading-relaxed">{linkUrl}</p>
                  <p className="text-[10px] text-blue-500 mt-1.5 leading-relaxed">
                    用户点击后直接跳转：公众号文章可直接打开，其他链接会提示复制到浏览器
                  </p>
                </div>
              )}
              外链提示注释结束 */}

              {/* 媒体 */}
              {mediaUrl && mediaType === 'video' && (
                <div className="bg-white rounded-xl p-2">
                  <video src={mediaUrl} controls className="w-full rounded-lg bg-black" />
                </div>
              )}
              {mediaUrl && mediaType === 'audio' && (
                <div className="bg-white rounded-xl px-3 py-3">
                  <audio src={mediaUrl} controls className="w-full h-7" />
                </div>
              )}

              {/* 正文 */}
              {hasBody ? (
                <div className="bg-white rounded-xl px-3 py-3">
                  {/* 摘要不再展示（2026-09-19 业务要求，与用户端详情页同步）：
                      预览要还原「用户实际看到的详情页」 */}
                  <article
                    className="content-body prose prose-sm max-w-none break-words text-gray-800"
                    style={{ fontSize: '11.5px', lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(bodyHtml) }}
                  />
                </div>
              ) : (
                <div className="bg-white rounded-xl px-3 py-6 flex flex-col items-center gap-1.5">
                  <ImageOff className="w-5 h-5 text-gray-300" />
                  <span className="text-[10px] text-gray-400">正文为空，填写后这里会实时显示</span>
                </div>
              )}

              {/* 底部操作条（模拟） */}
              <div className="bg-white rounded-xl px-3 py-2.5 flex items-center justify-around text-[9px] text-gray-300">
                <span>👍 点赞</span>
                <span>⭐ 有用</span>
                <span>❤️ 收藏</span>
                <span>↗ 分享</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-gray-400 mt-2 leading-relaxed px-1 shrink-0">
        预览为模拟效果，实际以用户端页面为准；窄屏下会自动隐藏。
      </p>
    </div>
  )
}

export default ContentPhonePreview
